/**
 * `/signin` — Sign in with Apple / Google / email magic link (spec §2.7),
 * then the 13+ age gate and account creation.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Icon, TopBar } from '../components/Widgets.tsx';
import { auth } from '../lib/auth.ts';
import { completeSignUp, refreshUser } from '../lib/session.ts';
import { toast, useApp } from '../lib/store.ts';

export function SignIn() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const next = search.get('next') ?? '/map';
  const user = useApp((s) => s.user);
  const signedIn = useApp((s) => s.signedIn);
  const authReady = useApp((s) => s.authReady);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [age, setAge] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void auth.completeMagicLink(location.href).then((done) => {
      if (done) void refreshUser();
    });
  }, []);

  useEffect(() => {
    if (user) navigate(next, { replace: true });
  }, [user, navigate, next]);

  const run = async (fn: () => Promise<void>): Promise<void> => {
    setBusy(true);
    try {
      await fn();
      await refreshUser();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Sign-in did not work. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!authReady) return <div className="page page--paper" />;

  return (
    <div className="page page--paper">
      <TopBar />
      <div className="sheet">
        {!signedIn ? (
          <section className="signin">
            <h1>Sign in to paint</h1>
            <p className="muted">Your email stays with the sign-in provider for account recovery. Draw Earth never sees it or stores it.</p>
            <button type="button" className="btn btn--big" disabled={busy} onClick={() => void run(() => auth.signInWithApple())} data-testid="signin-apple">
              Continue with Apple
            </button>
            <button type="button" className="btn btn--big btn--paper" disabled={busy} onClick={() => void run(() => auth.signInWithGoogle())} data-testid="signin-google">
              Continue with Google
            </button>
            <form
              className="card"
              onSubmit={(e) => {
                e.preventDefault();
                void run(async () => {
                  await auth.sendMagicLink(email);
                  setSent(true);
                });
              }}
            >
              <label>
                Or get a sign-in link by email
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
              </label>
              <div>
                <button type="submit" className="btn btn--paper" disabled={busy || !email}>
                  Email me a link
                </button>
              </div>
              {sent && <p className="muted">Sent. Open the link on this device to finish.</p>}
            </form>
            {auth.mode === 'dev' && <p className="pixel muted">dev build · any button signs you in locally</p>}
          </section>
        ) : (
          <section className="signin" data-testid="age-gate">
            <h1>Almost there</h1>
            <label className="check">
              <input type="checkbox" checked={age} onChange={(e) => setAge(e.target.checked)} data-testid="age-check" /> I'm 13 or older
            </label>
            <p className="muted">
              You also agree to the <Link to="/terms">terms</Link> and <Link to="/privacy">privacy policy</Link>. You'll get a random two-word handle; no real names here.
            </p>
            <div className="row">
              <button type="button" className="btn btn--big" disabled={!age || busy} onClick={() => void run(() => completeSignUp())} data-testid="create-account">
                <Icon name="spark" /> Create my account
              </button>
              <button type="button" className="btn btn--paper" disabled={busy} onClick={() => void auth.signOut().then(refreshUser)}>
                Cancel
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
